import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import SidebarMenu from './common/menus/SidebarMenu';
import { useAuth } from '../contexts/AuthContext';
import ColegioService from '../services/ColegioService';
import ParticularService from '../services/ParticularService';
import colegioApiService from '../services/ColegioApiService';
import solicitudesService from '../services/SolicitudesService';
import particularesApiService from '../services/ParticularesApiService';
import { ADMIN_DASHBOARD_MENU_ITEMS, navigateAdminMenuItem, isAdminColegio } from '../utils/adminMenu';

const PARTICULAR_MENU_ITEMS = [
  { id: 'resumen', label: 'Resumen', icon: 'fas fa-chart-line' },
  { id: 'etiquetas', label: 'Etiquetas', icon: 'fas fa-tags' },
  { id: 'cursos', label: 'Cursos', icon: 'fas fa-layer-group' },
  { id: 'profesoresAsociados', label: 'Profesores', icon: 'fas fa-user-tie' },
  { id: 'alumnos', label: 'Alumnos', icon: 'fas fa-user-graduate' },
  { id: 'aulas', label: 'Aulas', icon: 'fas fa-door-open' },
  { id: 'asistentes', label: 'Asistentes IA', icon: 'fas fa-robot' }
];

const getSolicitudFileDisplayName = (item) => {
  const nombreVisible = item?.nombreVisible || item?.NombreVisible || '';
  if (nombreVisible) return nombreVisible;
  const nombreArchivo = item?.nombreArchivo || item?.NombreArchivo || '';
  if (!nombreArchivo) return 'Archivo PDF';
  const fileName = nombreArchivo.split(/[\\/]/).pop() || nombreArchivo;
  return /^[a-f0-9]{32}_/i.test(fileName) ? fileName.slice(33) : fileName;
};

const normalizeSolicitudFiles = (files) => {
  if (!Array.isArray(files)) return [];
  return files.map((item, index) => ({
    id: item?.id || item?.Id || `file-${index}`,
    nombreArchivo: item?.nombreArchivo || item?.NombreArchivo || '',
    nombreVisible: getSolicitudFileDisplayName(item)
  }));
};

const normalizeSolicitudChanges = (changes) => {
  if (!Array.isArray(changes)) return [];
  return changes.map((item, index) => ({
    id: item?.id || item?.Id || `change-${index}`,
    campo: item?.campo || item?.Campo || '',
    valorAnterior: item?.valorAnterior || item?.ValorAnterior || '',
    valorNuevo: item?.valorNuevo || item?.ValorNuevo || ''
  }));
};

const formatChangeValue = (value) => {
  if (!value) return '—';
  const normalized = String(value).replace(/\s+/g, ' ').trim();
  if (!normalized) return '—';
  return normalized.length > 180 ? `${normalized.slice(0, 177)}...` : normalized;
};

const formatSolicitudTipo = (value) => {
  const raw = String(value || 'Solicitud').trim();
  const normalized = raw.toLowerCase();
  if (normalized.includes('creación gpt') || normalized.includes('creacion gpt')) return 'Creación de asistente IA';
  if (normalized.includes('edición gpt') || normalized.includes('edicion gpt')) return 'Edición de asistente IA';
  if (normalized.includes('eliminación gpt') || normalized.includes('eliminacion gpt')) return 'Eliminación de asistente IA';
  return raw.replace(/GPT Clásico/gi, 'asistente IA').replace(/GPT clásico/gi, 'asistente IA');
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const buildSolicitudZipName = (selected) => {
  const rawName = selected?.gptClasicoNombre || selected?.GPTClasicoNombre || 'gpt-clasico';
  const safeName = String(rawName).replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '');
  return `${safeName || 'gpt-clasico'}-archivos-agregar.zip`;
};

const getSolicitudAudienceMeta = (item, isParticularMode = false) => {
  if (isParticularMode) {
    return {
      className: 'shared',
      shortLabel: 'Particular',
      longLabel: 'Asistente particular',
      description: 'Podrá vincularse a las aulas particulares que defina el dueño de la cuenta o sus profesores.'
    };
  }

  const isOwn = Boolean(item?.esPropio ?? item?.EsPropio ?? false);
  if (isOwn) {
    return {
      className: 'own',
      shortLabel: 'Propio',
      longLabel: 'Asistente propio',
      description: 'Será de uso personal del docente. No podrá compartirse con estudiantes desde el aula.'
    };
  }

  return {
    className: 'shared',
    shortLabel: 'Compartible',
    longLabel: 'Visible en aula',
    description: 'Podrá vincularse a aulas y mostrarse a estudiantes según la configuración del docente.'
  };
};

const AdminSolicitudesInbox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const normalizedRoleName = (user?.roleName || user?.nombreRol || user?.role || user?.rol || '').toLowerCase().trim();
  const isGeneralAdmin = normalizedRoleName.includes('admin') && !isAdminColegio(user);
  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId(), []);
  const activeParticular = useMemo(() => ParticularService.getActiveParticular(), []);
  const activeParticularId = useMemo(() => ParticularService.getActiveParticularId(), []);
  const isParticularMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const values = [
      location.state?.tipoTenant,
      location.state?.contexto,
      location.state?.modo,
      params.get('tipoTenant'),
      params.get('contexto'),
      params.get('modo')
    ];
    return values.some((value) => String(value || '').toLowerCase().includes('particular')) ||
      (Boolean(activeParticularId) && !activeColegioId);
  }, [activeColegioId, activeParticularId, location.search, location.state]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const activeParticularName = activeParticular?.nombre || activeParticular?.Nombre || activeParticular?.displayName || activeParticular?.DisplayName || 'Particular';
  const contextName = isParticularMode ? activeParticularName : activeColegioName;
  const [solicitudes, setSolicitudes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterLeido, setFilterLeido] = useState('todos');
  const [filterTipo, setFilterTipo] = useState('todos');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [totalPendientes, setTotalPendientes] = useState(0);
  const [acciones, setAcciones] = useState([]);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyHeader, setApiKeyHeader] = useState('X-Api-Key');
  const [accionesLoading, setAccionesLoading] = useState(false);
  const [accionesError, setAccionesError] = useState('');
  const [assistantId, setAssistantId] = useState('');
  const [urlPublica, setUrlPublica] = useState('');
  const [finalizarModal, setFinalizarModal] = useState(false);
  const [finalizarLoading, setFinalizarLoading] = useState(false);
  const [toggleReadModal, setToggleReadModal] = useState({ open: false, nextLeido: true });
  const [toggleReadLoading, setToggleReadLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, solicitud: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [downloadingFileKey, setDownloadingFileKey] = useState('');
  const [downloadingZip, setDownloadingZip] = useState(false);
  const searchDebounceRef = useRef(null);

  const pollMinutes = useMemo(() => {
    const raw = process.env.REACT_APP_ADMIN_SOLICITUDES_POLL_MINUTES;
    const parsed = raw ? Number(raw) : 1;
    if (!Number.isFinite(parsed) || parsed <= 0) return 1;
    return parsed;
  }, []);

  useEffect(() => {
    if (!isGeneralAdmin) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [isGeneralAdmin, navigate]);

  useEffect(() => {
    if (!isGeneralAdmin) return;

    if (isParticularMode) {
      if (!activeParticularId) {
        navigate('/admin/select-colegio', { replace: true, state: { scope: 'particulares' } });
      }
      return;
    }

    if (!activeColegioId) {
      navigate('/admin/select-colegio', { replace: true, state: { scope: 'colegios' } });
    }
  }, [activeColegioId, activeParticularId, isGeneralAdmin, isParticularMode, navigate]);

  useEffect(() => {
    if (location.state?.solicitudId && solicitudes.length > 0) {
      const match = solicitudes.find((item) => (item.id || item.Id) === location.state.solicitudId);
      if (match) {
        setSelected(match);
      }
    }
  }, [location.state, solicitudes]);

  useEffect(() => {
    const loadAcciones = async () => {
      const hasTenantContext = isParticularMode ? activeParticularId : activeColegioId;
      if (!isGeneralAdmin || !selected || !hasTenantContext) {
        setAcciones([]);
        setApiKey('');
        setApiKeyHeader('X-Api-Key');
        setAssistantId('');
        setUrlPublica('');
        return;
      }
      const id = selected.id || selected.Id;
      if (!id) return;
      setAccionesLoading(true);
      setAccionesError('');
      try {
        const response = isParticularMode
          ? await particularesApiService.getSolicitudesRecibidasAcciones(id)
          : await solicitudesService.getSolicitudesRecibidasAcciones(id, activeColegioId);
        const list = response?.acciones || response?.Acciones || [];
        const key = response?.apiKey || response?.ApiKey || '';
        const headerName = response?.headerName || response?.HeaderName || 'X-Api-Key';
        const assistant = response?.idOpenAIAssistant || response?.IdOpenAIAssistant || '';
        setAcciones(Array.isArray(list) ? list : []);
        setApiKey(key || '');
        setApiKeyHeader(headerName);
        setAssistantId(assistant);

        if (!isParticularMode && !key && activeColegioId) {
          const apiRes = await colegioApiService.getColegioApiKey(activeColegioId);
          const apiKeyFromColegio = apiRes?.apiKey || apiRes?.ApiKey || '';
          const headerFromColegio = apiRes?.headerName || apiRes?.HeaderName || 'X-Api-Key';
          if (apiKeyFromColegio) {
            setApiKey(apiKeyFromColegio);
          }
          setApiKeyHeader(headerFromColegio);
        }
        const existingUrl = selected.gptClasicoURLPublica || selected.GPTClasicoURLPublica || '';
        setUrlPublica(existingUrl || '');
      } catch (err) {
        setAccionesError(err?.message || 'No pudimos cargar las acciones.');
        setAcciones([]);
        setApiKey('');
        setApiKeyHeader('X-Api-Key');
        setAssistantId('');
        setUrlPublica('');
      } finally {
        setAccionesLoading(false);
      }
    };
    loadAcciones();
  }, [activeColegioId, activeParticularId, isGeneralAdmin, isParticularMode, selected]);

  const loadSolicitudes = useCallback(async () => {
    if (!isGeneralAdmin) return;
    if (isParticularMode && !activeParticularId) return;
    if (!isParticularMode && !activeColegioId) return;
    setLoading(true);
    setError('');
    try {
      const request = {
        skip: 0,
        take: 50,
        search,
        leido: filterLeido,
        tipo: filterTipo
      };
      const response = isParticularMode
        ? await particularesApiService.getSolicitudesRecibidasGptClasicos(request)
        : await solicitudesService.getSolicitudesRecibidasGptClasicos({
          ...request,
          colegioId: activeColegioId
        });
      const items = response?.items || response?.Items || [];
      const pendientes = response?.totalPendientes ?? response?.TotalPendientes ?? 0;
      setSolicitudes(Array.isArray(items) ? items : []);
      setTotalPendientes(Number.isFinite(pendientes) ? pendientes : 0);
      if (Array.isArray(items) && items.length > 0) {
        setSelected((prev) => {
          if (!prev) return items[0];
          const prevId = prev.id || prev.Id;
          return items.find((item) => (item.id || item.Id) === prevId) || items[0];
        });
      } else {
        setSelected(null);
      }
    } catch (err) {
      setError(err?.message || 'No pudimos cargar las solicitudes.');
      setSolicitudes([]);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  }, [activeColegioId, activeParticularId, filterLeido, filterTipo, isGeneralAdmin, isParticularMode, search]);

  const handleBackToSelectColegio = useCallback(() => {
    navigate('/admin/select-colegio', {
      replace: true,
      state: { scope: isParticularMode ? 'particulares' : 'colegios' }
    });
  }, [isParticularMode, navigate]);

  const handleParticularMenuNavigate = useCallback((item) => {
    if (!item) return;
    setIsSidebarOpen(false);

    if (item.path) {
      navigate(item.path);
      return;
    }

    navigate('/particular', { state: { tab: item.id } });
  }, [navigate]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      loadSolicitudes();
    }, 400);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search, filterLeido, filterTipo, loadSolicitudes]);

  useEffect(() => {
    if (!isGeneralAdmin) return undefined;
    loadSolicitudes();
    const intervalMs = pollMinutes * 60 * 1000;
    const intervalId = setInterval(loadSolicitudes, intervalMs);
    return () => clearInterval(intervalId);
  }, [isGeneralAdmin, loadSolicitudes, pollMinutes]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
  };

  const getSolicitudEstado = (item) => (
    item?.estado ||
    item?.Estado ||
    (item?.fechaFinalizacion || item?.FechaFinalizacion ? 'Finalizada' : 'En proceso')
  );

  const getSolicitudEstadoClassName = (itemOrEstado) => {
    const estado = typeof itemOrEstado === 'string'
      ? itemOrEstado
      : getSolicitudEstado(itemOrEstado);
    return String(estado).toLowerCase().includes('final') ? 'is-finalizada' : 'is-proceso';
  };

  const isUnread = (item) => !(item?.leido ?? item?.Leido);
  const handleSolicitudItemKeyDown = (event, item) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setSelected(item);
  };

  const selectedArchivosActuales = useMemo(
    () => normalizeSolicitudFiles(selected?.archivosActuales || selected?.ArchivosActuales),
    [selected]
  );
  const selectedArchivosAgregar = useMemo(
    () => normalizeSolicitudFiles(selected?.archivosAgregar || selected?.ArchivosAgregar),
    [selected]
  );
  const selectedArchivosQuitar = useMemo(
    () => normalizeSolicitudFiles(selected?.archivosQuitar || selected?.ArchivosQuitar),
    [selected]
  );
  const selectedCambiosDetectados = useMemo(
    () => normalizeSolicitudChanges(selected?.cambiosDetectados || selected?.CambiosDetectados),
    [selected]
  );
  const isSelectedFinalizadaConUrl = useMemo(() => {
    const fechaFinalizacion = selected?.fechaFinalizacion || selected?.FechaFinalizacion;
    return Boolean(fechaFinalizacion && String(urlPublica || '').trim());
  }, [selected, urlPublica]);
  const isSelectedEditRequest = useMemo(() => {
    const tipo = selected?.tipoSolicitud || selected?.TipoSolicitud || '';
    return String(tipo).toLowerCase().includes('edición') || String(tipo).toLowerCase().includes('edicion');
  }, [selected]);
  const selectedAudienceMeta = useMemo(() => getSolicitudAudienceMeta(selected, isParticularMode), [isParticularMode, selected]);

  const handleDownloadSolicitudArchivoAgregar = async (fileIndex, fileName) => {
    if (!selected) return;
    const solicitudId = selected.id || selected.Id;
    if (!solicitudId) return;
    const fileKey = `add-${fileIndex}`;
    setDownloadingFileKey(fileKey);
    setError('');
    try {
      const blob = isParticularMode
        ? await particularesApiService.downloadSolicitudArchivoAgregar(solicitudId, fileIndex)
        : await solicitudesService.downloadSolicitudArchivoAgregar(solicitudId, fileIndex, activeColegioId);
      downloadBlob(blob, fileName || `archivo-${fileIndex + 1}.pdf`);
    } catch (err) {
      setError(err?.message || 'No pudimos descargar el archivo.');
    } finally {
      setDownloadingFileKey('');
    }
  };

  const handleDownloadSolicitudArchivosZip = async () => {
    if (!selected) return;
    const solicitudId = selected.id || selected.Id;
    if (!solicitudId) return;
    setDownloadingZip(true);
    setError('');
    try {
      const blob = isParticularMode
        ? await particularesApiService.downloadSolicitudArchivosAgregarZip(solicitudId)
        : await solicitudesService.downloadSolicitudArchivosAgregarZip(solicitudId, activeColegioId);
      downloadBlob(blob, buildSolicitudZipName(selected));
    } catch (err) {
      setError(err?.message || 'No pudimos descargar el ZIP.');
    } finally {
      setDownloadingZip(false);
    }
  };

  const openToggleReadModal = (nextLeido) => {
    setToggleReadModal({ open: true, nextLeido });
  };

  const closeToggleReadModal = () => {
    if (toggleReadLoading) return;
    setToggleReadModal({ open: false, nextLeido: true });
  };

  const confirmToggleRead = async () => {
    if (!selected) return;
    const id = selected.id || selected.Id;
    if (!id) return;
    setToggleReadLoading(true);
    try {
      if (isParticularMode) {
        await particularesApiService.updateSolicitudLeido(id, toggleReadModal.nextLeido);
      } else {
        await solicitudesService.updateSolicitudLeido(id, toggleReadModal.nextLeido);
      }
      setSelected((prev) => prev ? { ...prev, leido: toggleReadModal.nextLeido } : prev);
      setSolicitudes((prev) => prev.map((item) => {
        const itemId = item.id || item.Id;
        if (itemId !== id) return item;
        return { ...item, leido: toggleReadModal.nextLeido };
      }));
    } catch (err) {
      setError(err?.message || 'No pudimos actualizar el estado de lectura.');
    } finally {
      setToggleReadLoading(false);
      setToggleReadModal({ open: false, nextLeido: true });
    }
  };

  const openDeleteSolicitudModal = (item, event) => {
    if (event) {
      event.stopPropagation();
    }
    setDeleteModal({ open: true, solicitud: item });
  };

  const closeDeleteSolicitudModal = () => {
    if (deleteLoading) return;
    setDeleteModal({ open: false, solicitud: null });
  };

  const confirmDeleteSolicitud = async () => {
    const item = deleteModal.solicitud;
    const id = item?.id || item?.Id;
    if (!id) return;

    setDeleteLoading(true);
    setError('');
    try {
      if (isParticularMode) {
        await particularesApiService.deleteSolicitudRecibida(id);
      } else {
        await solicitudesService.deleteSolicitudRecibida(id, activeColegioId);
      }
      setDeleteModal({ open: false, solicitud: null });
      setSolicitudes((prev) => prev.filter((solicitud) => (solicitud.id || solicitud.Id) !== id));
      setSelected((prev) => ((prev?.id || prev?.Id) === id ? null : prev));
      if (!(item?.fechaFinalizacion || item?.FechaFinalizacion)) {
        setTotalPendientes((prev) => Math.max(prev - 1, 0));
      }
      await loadSolicitudes();
    } catch (err) {
      setError(err?.message || 'No pudimos eliminar la solicitud.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openFinalizarModal = () => {
    setFinalizarModal(true);
  };

  const closeFinalizarModal = () => {
    if (finalizarLoading) return;
    setFinalizarModal(false);
  };

  const confirmFinalizar = async () => {
    if (!selected) return;
    const id = selected.id || selected.Id;
    if (!id) return;
    if (!assistantId) {
      setError('No encontramos el IdOpenAIAssistant para finalizar la solicitud.');
      return;
    }
    if (!urlPublica?.trim()) {
      setError('La URL pública es obligatoria para finalizar la solicitud.');
      return;
    }
    setFinalizarLoading(true);
    try {
      const payload = {
        idOpenAIAssistant: assistantId,
        urlPublica: urlPublica.trim()
      };
      if (isParticularMode) {
        await particularesApiService.finalizarSolicitud(id, payload);
      } else {
        await solicitudesService.finalizarSolicitud(id, payload);
      }
      const finalDate = new Date().toISOString();
      setSelected((prev) => prev ? {
        ...prev,
        fechaFinalizacion: finalDate,
        GPTClasicoURLPublica: urlPublica.trim(),
        gptClasicoURLPublica: urlPublica.trim(),
        estado: 'Finalizada',
        leido: true,
        Leido: true
      } : prev);
      setSolicitudes((prev) => prev.map((item) => {
        const itemId = item.id || item.Id;
        if (itemId !== id) return item;
        return {
          ...item,
          fechaFinalizacion: finalDate,
          GPTClasicoURLPublica: urlPublica.trim(),
          gptClasicoURLPublica: urlPublica.trim(),
          estado: 'Finalizada',
          leido: true,
          Leido: true
        };
      }));
      setFinalizarModal(false);
    } catch (err) {
      setError(err?.message || 'No pudimos finalizar la solicitud.');
    } finally {
      setFinalizarLoading(false);
    }
  };

  return (
    <div className={`new-dashboard ${isParticularMode ? 'particular-dashboard' : 'admin-mode'}`}>
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>
        {isParticularMode ? (
          <SidebarMenu
            items={PARTICULAR_MENU_ITEMS}
            activeId="asistentes"
            onNavigate={handleParticularMenuNavigate}
          />
        ) : (
          <AdminMenu
            activeId="procesos-asincronicos"
            user={user}
            featureFlags={ColegioService.getFeatureFlags()}
            baseItems={ADMIN_DASHBOARD_MENU_ITEMS}
            onNavigate={(item) => {
              setIsSidebarOpen(false);
              navigateAdminMenuItem(navigate, item);
            }}
          />
        )}
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className={isParticularMode ? 'fas fa-user-tie' : 'fas fa-user-shield'}></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">{isParticularMode ? 'Particular' : 'Admin'}</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main access-inbox-main">
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">Solicitudes recibidas</h1>
            <p className="page-subtitle">Bandeja de entrada de solicitudes de asistentes IA para {contextName}.</p>
          </div>
          <div className="header-right">
            <button
              type="button"
              className="inbox-btn-header"
              onClick={loadSolicitudes}
              title="Actualizar"
            >
              <i className="fas fa-rotate"></i>
              Actualizar
              {totalPendientes > 0 && (
                <span className="inbox-badge">{totalPendientes}</span>
              )}
            </button>
            {!isParticularMode && <ActiveColegioBadge />}
            <UserSessionMenu
              user={user}
              onLogout={logout}
              extraItems={isGeneralAdmin ? [{
                key: 'switch-tenant',
                label: isParticularMode ? 'Cambiar particular' : 'Cambiar colegio',
                icon: 'fas fa-exchange-alt',
                onClick: handleBackToSelectColegio
              }] : []}
              showStandaloneLogout={false}
              userFallbackLabel="Administrador"
            />
          </div>
        </header>

        <div className="dashboard-content access-inbox-content">
          <div className="access-inbox-card access-inbox-page">
            <div className="access-inbox-header">
              <div>
                <h3>Solicitudes de asistentes IA</h3>
                <p>{solicitudes.length} solicitudes • {totalPendientes} en proceso</p>
              </div>
            </div>

            <div className="access-inbox-toolbar">
              <div className="access-search">
                <i className="fas fa-search" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, tipo o solicitante..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="access-filters">
                <select value={filterLeido} onChange={(e) => setFilterLeido(e.target.value)}>
                  <option value="todos">Todos</option>
                  <option value="false">No leídas</option>
                  <option value="true">Leídas</option>
                </select>
                <select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)}>
                  <option value="todos">Todos</option>
                  <option value="creacion">Creación</option>
                  <option value="edicion">Actualización</option>
                  <option value="eliminacion">Eliminación</option>
                </select>
              </div>
            </div>

            <div className="access-inbox-body">
              <div className="access-inbox-list">
                {loading && <div className="access-inbox-loading">Cargando...</div>}
                {error && <div className="access-inbox-error">{error}</div>}
                {!loading && !error && solicitudes.length === 0 && (
                  <div className="access-inbox-empty">No hay solicitudes.</div>
                )}
                {solicitudes.map((item) => {
                  const id = item.id || item.Id;
                  const nombre = item.gptClasicoNombre || item.GPTClasicoNombre || 'Asistente IA';
                  const solicitante = item.usuarioNombre || item.UsuarioNombre || 'Solicitante';
                  const tipo = formatSolicitudTipo(item.tipoSolicitud || item.TipoSolicitud || 'Solicitud');
                  const fecha = item.fechaCreacion || item.FechaCreacion;
                  const estado = getSolicitudEstado(item);
                  const estadoClassName = getSolicitudEstadoClassName(estado);
                  const audienceMeta = getSolicitudAudienceMeta(item, isParticularMode);
                  return (
                    <div
                      key={id}
                      role="button"
                      tabIndex={0}
                      className={`access-inbox-item ${isUnread(item) ? 'is-unread' : 'is-read'} ${(selected?.id || selected?.Id) === id ? 'is-active' : ''}`}
                      onClick={() => setSelected(item)}
                      onKeyDown={(event) => handleSolicitudItemKeyDown(event, item)}
                    >
                      <div className="access-item-header">
                        <span className="access-item-name">{nombre}</span>
                        <div className="access-item-tags">
                          <span className={`access-item-visibility ${audienceMeta.className}`}>
                            {audienceMeta.shortLabel}
                          </span>
                          <span className={`access-item-badge ${isUnread(item) ? 'unread' : 'read'}`}>
                            {isUnread(item) ? 'No leído' : 'Leído'}
                          </span>
                        </div>
                      </div>
                      <div className="access-item-meta">
                        <span>{solicitante}</span>
                        <span>{tipo}</span>
                        <span>{formatDateTime(fecha)}</span>
                      </div>
                      <div className="access-item-footer">
                        <div className={`access-item-preview access-status-text ${estadoClassName}`}>{estado}</div>
                        <button
                          type="button"
                          className="access-item-delete-btn"
                          onClick={(event) => openDeleteSolicitudModal(item, event)}
                          title="Eliminar solicitud"
                          aria-label={`Eliminar solicitud ${nombre}`}
                        >
                          <i className="fas fa-trash"></i>
                          Eliminar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="access-inbox-detail">
                {loading && <div className="access-inbox-loading">Cargando detalle...</div>}
                {!loading && !selected && (
                  <div className="access-inbox-empty">Selecciona una solicitud para ver el detalle.</div>
                )}
                {!loading && selected && (
                  <div className="access-detail-card">
                    <div className="access-detail-header">
                      <div>
                        <h4>{selected.gptClasicoNombre || selected.GPTClasicoNombre}</h4>
                        <div className="access-detail-title-meta">
                          <p>{selected.tipoSolicitud || selected.TipoSolicitud}</p>
                          <span className={`access-detail-visibility ${selectedAudienceMeta.className}`}>
                            {selectedAudienceMeta.longLabel}
                          </span>
                        </div>
                      </div>
                      <span className={`access-detail-status ${isUnread(selected) ? 'unread' : 'read'}`}>
                        {isUnread(selected) ? 'No leído' : 'Leído'}
                      </span>
                    </div>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className={`btn ${isUnread(selected) ? 'btn-success' : 'btn-danger'}`}
                        onClick={() => openToggleReadModal(isUnread(selected))}
                      >
                        {isUnread(selected) ? 'Leer' : 'No leer'}
                      </button>
                    </div>
                    <div className="access-detail-meta">
                      <div>
                        <span className="access-detail-label">Solicitante</span>
                        <span>{selected.usuarioNombre || selected.UsuarioNombre || '—'}</span>
                      </div>
                      {isParticularMode && (
                        <div>
                          <span className="access-detail-label">Dueño de cuenta</span>
                          <span>
                            {selected.duenoCuentaNombre || selected.DuenoCuentaNombre || '—'}
                            {(selected.duenoCuentaEmail || selected.DuenoCuentaEmail) && (
                              <> · {selected.duenoCuentaEmail || selected.DuenoCuentaEmail}</>
                            )}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="access-detail-label">Estado</span>
                        <span className={`access-status-pill ${getSolicitudEstadoClassName(selected)}`}>
                          {getSolicitudEstado(selected)}
                        </span>
                      </div>
                      <div>
                        <span className="access-detail-label">Fecha</span>
                        <span>{formatDateTime(selected.fechaCreacion || selected.FechaCreacion)}</span>
                      </div>
                      {!isParticularMode && (
                        <>
                          <div>
                            <span className="access-detail-label">Área</span>
                            <span>{selected.nombreArea || selected.NombreArea || '—'}</span>
                          </div>
                          <div>
                            <span className="access-detail-label">Materia</span>
                            <span>{selected.nombreMateria || selected.NombreMateria || '—'}</span>
                          </div>
                        </>
                      )}
                      <div>
                        <span className="access-detail-label">Alcance</span>
                        <span>{selectedAudienceMeta.longLabel}</span>
                      </div>
                    </div>
                    <div className={`access-detail-visibility-card ${selectedAudienceMeta.className}`}>
                      <span className="access-detail-label">{isParticularMode ? 'Uso del asistente' : 'Impacto para estudiantes'}</span>
                      <strong>{selectedAudienceMeta.longLabel}</strong>
                      <p>{selectedAudienceMeta.description}</p>
                    </div>
                    <div className="access-detail-content">
                      <span className="access-detail-label">Descripción</span>
                      <p>{selected.gptClasicoDescripcion || selected.GPTClasicoDescripcion || '—'}</p>
                    </div>
                    <div className="access-detail-content">
                      <span className="access-detail-label">Instrucciones</span>
                      <textarea
                        className="form-input"
                        rows="6"
                        readOnly
                        value={selected.gptClasicoInstrucciones || selected.GPTClasicoInstrucciones || '—'}
                      />
                    </div>
                    {isSelectedEditRequest && (
                      <div className="access-detail-content">
                        <span className="access-detail-label">Cambios a aplicar</span>
                        {selectedCambiosDetectados.length === 0 && selectedArchivosAgregar.length === 0 && selectedArchivosQuitar.length === 0 ? (
                          <p>No se detectaron cambios concretos en esta solicitud.</p>
                        ) : (
                          <div style={{ display: 'grid', gap: '0.75rem' }}>
                            {selectedCambiosDetectados.map((change) => (
                              <div
                                key={change.id}
                                style={{
                                  border: '1px solid rgba(148, 163, 184, 0.25)',
                                  borderRadius: '12px',
                                  padding: '0.85rem 1rem',
                                  background: 'rgba(248, 250, 252, 0.9)'
                                }}
                              >
                                <strong style={{ display: 'block', marginBottom: '0.4rem' }}>{change.campo}</strong>
                                <div style={{ display: 'grid', gap: '0.25rem', fontSize: '0.92rem', color: '#334155' }}>
                                  <span><strong>Actual:</strong> {formatChangeValue(change.valorAnterior)}</span>
                                  <span><strong>Nuevo:</strong> {formatChangeValue(change.valorNuevo)}</span>
                                </div>
                              </div>
                            ))}
                            {selectedArchivosQuitar.length > 0 && (
                              <div className="access-item-preview" style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#b91c1c' }}>
                                Debes quitar {selectedArchivosQuitar.length} archivo{selectedArchivosQuitar.length === 1 ? '' : 's'}.
                              </div>
                            )}
                            {selectedArchivosAgregar.length > 0 && (
                              <div className="access-item-preview" style={{ background: 'rgba(34, 197, 94, 0.08)', color: '#166534' }}>
                                Debes agregar {selectedArchivosAgregar.length} archivo{selectedArchivosAgregar.length === 1 ? '' : 's'}.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {(selectedArchivosActuales.length > 0 || selectedArchivosAgregar.length > 0 || selectedArchivosQuitar.length > 0) && (
                      <div className="access-detail-content">
                        <span className="access-detail-label">Archivos de la solicitud</span>
                        <div style={{ display: 'grid', gap: '1rem' }}>
                          <div>
                            <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Archivos actuales</strong>
                            {selectedArchivosActuales.length === 0 ? (
                              <p>—</p>
                            ) : (
                              <div style={{ display: 'grid', gap: '0.5rem' }}>
                                {selectedArchivosActuales.map((file) => (
                                  <div key={`actual-${file.id}`} className="access-item-preview">
                                    {file.nombreVisible}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Archivos a quitar</strong>
                            {selectedArchivosQuitar.length === 0 ? (
                              <p>—</p>
                            ) : (
                              <div style={{ display: 'grid', gap: '0.5rem' }}>
                                {selectedArchivosQuitar.map((file) => (
                                  <div
                                    key={`remove-${file.id}`}
                                    className="access-item-preview"
                                    style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#b91c1c' }}
                                  >
                                    {file.nombreVisible}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                              <strong>Archivos a agregar</strong>
                              {selectedArchivosAgregar.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline"
                                  onClick={handleDownloadSolicitudArchivosZip}
                                  disabled={downloadingZip || !!downloadingFileKey}
                                  style={{ padding: '0.45rem 0.8rem' }}
                                >
                                  {downloadingZip ? 'Descargando ZIP...' : 'Descargar ZIP'}
                                </button>
                              )}
                            </div>
                            {selectedArchivosAgregar.length === 0 ? (
                              <p>—</p>
                            ) : (
                              <div style={{ display: 'grid', gap: '0.5rem' }}>
                                {selectedArchivosAgregar.map((file, index) => {
                                  const fileKey = `add-${index}`;
                                  const isDownloadingFile = downloadingFileKey === fileKey;
                                  return (
                                  <div
                                    key={`add-${file.id}`}
                                    className="access-item-preview"
                                    style={{
                                      background: 'rgba(34, 197, 94, 0.08)',
                                      color: '#166534',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: '0.75rem',
                                      flexWrap: 'wrap'
                                    }}
                                  >
                                    <span style={{ flex: '1 1 260px', minWidth: 0 }}>{file.nombreVisible}</span>
                                    <button
                                      type="button"
                                      className="btn btn-outline"
                                      onClick={() => handleDownloadSolicitudArchivoAgregar(index, file.nombreVisible)}
                                      disabled={isDownloadingFile || downloadingZip}
                                      style={{ padding: '0.45rem 0.8rem' }}
                                    >
                                      {isDownloadingFile ? 'Descargando...' : 'Descargar'}
                                    </button>
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="access-detail-meta">
                      <div>
                        <span className="access-detail-label">Modelo</span>
                        <span>{selected.gptClasicoModelo || selected.GPTClasicoModelo || '—'}</span>
                      </div>
                      <div>
                        <span className="access-detail-label">Frase inicial</span>
                        <span>{selected.gptClasicoFraseIniciarConversacion || selected.GPTClasicoFraseIniciarConversacion || '—'}</span>
                      </div>
                    </div>
                    <div className="access-detail-meta">
                      <div>
                        <span className="access-detail-label">Web</span>
                        <span>{selected.gptClasicoBusquedaEnLaWeb || selected.GPTClasicoBusquedaEnLaWeb ? 'Sí' : 'No'}</span>
                      </div>
                      <div>
                        <span className="access-detail-label">Lienzo</span>
                        <span>{selected.gptClasicoLienzo || selected.GPTClasicoLienzo ? 'Sí' : 'No'}</span>
                      </div>
                      <div>
                        <span className="access-detail-label">Imágenes</span>
                        <span>{selected.gptClasicoGeneracionDeImagenes || selected.GPTClasicoGeneracionDeImagenes ? 'Sí' : 'No'}</span>
                      </div>
                      <div>
                        <span className="access-detail-label">Interprete</span>
                        <span>{selected.gptClasicoInterpreteCodigoYAnalisisDatos || selected.GPTClasicoInterpreteCodigoYAnalisisDatos ? 'Sí' : 'No'}</span>
                      </div>
                    </div>
                    <div className="access-detail-content">
                      <span className="access-detail-label">{apiKeyHeader}</span>
                      <input
                        type="text"
                        className="form-input"
                        readOnly
                        value={apiKey || '—'}
                      />
                    </div>
                    <div className="access-detail-content">
                      <span className="access-detail-label">Actions</span>
                      {accionesLoading && <p>Cargando actions...</p>}
                      {accionesError && <p>{accionesError}</p>}
                      {!accionesLoading && !accionesError && acciones.length === 0 && <p>—</p>}
                      {!accionesLoading && !accionesError && acciones.length > 0 && (
                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                          {acciones.map((action, idx) => (
                            <textarea
                              key={`action-${idx}`}
                              className="form-input"
                              rows="8"
                              readOnly
                              value={action}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="access-detail-content">
                      <span className="access-detail-label">URL pública</span>
                      <input
                        type="text"
                        className="form-input"
                        value={urlPublica}
                        onChange={(e) => setUrlPublica(e.target.value)}
                        placeholder="Pegá la URL pública del asistente"
                      />
                    </div>
                    <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={openFinalizarModal}
                        disabled={finalizarLoading || isSelectedFinalizadaConUrl}
                      >
                        {isSelectedFinalizadaConUrl ? 'Solicitud finalizada' : 'Finalizar Solicitud'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {toggleReadModal.open && (
          <div className="modal-overlay" onClick={closeToggleReadModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  <i className="fas fa-envelope-open-text"></i>
                  {toggleReadModal.nextLeido ? 'Marcar como leído' : 'Marcar como no leído'}
                </h3>
                <button type="button" className="modal-close-btn" onClick={closeToggleReadModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  {toggleReadModal.nextLeido
                    ? 'Esta acción marcará la solicitud como leída y dejará de figurar como pendiente.'
                    : 'Esta acción marcará la solicitud como no leída y volverá a figurar como pendiente.'}
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeToggleReadModal} disabled={toggleReadLoading}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className={`btn ${toggleReadModal.nextLeido ? 'btn-success' : 'btn-danger'}`}
                  onClick={confirmToggleRead}
                  disabled={toggleReadLoading}
                >
                  {toggleReadLoading ? 'Guardando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}
        {finalizarModal && (
          <div className="modal-overlay" onClick={closeFinalizarModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  <i className="fas fa-check-circle"></i>
                  Finalizar solicitud
                </h3>
                <button type="button" className="modal-close-btn" onClick={closeFinalizarModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Esta acción marcará la solicitud como finalizada, asociará la URL pública ingresada y actualizará el asistente.
                  ¿Querés continuar?
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeFinalizarModal} disabled={finalizarLoading}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={confirmFinalizar}
                  disabled={finalizarLoading}
                >
                  {finalizarLoading ? 'Finalizando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}
        {deleteModal.open && (
          <div className="modal-overlay" onClick={closeDeleteSolicitudModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  <i className="fas fa-trash"></i>
                  Eliminar solicitud
                </h3>
                <button type="button" className="modal-close-btn" onClick={closeDeleteSolicitudModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Se eliminará la solicitud
                  {' '}
                  <strong>{deleteModal.solicitud?.gptClasicoNombre || deleteModal.solicitud?.GPTClasicoNombre || 'seleccionada'}</strong>
                  {' '}
                  de la bandeja. Esta acción no modifica el asistente GPT asociado.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeDeleteSolicitudModal} disabled={deleteLoading}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDeleteSolicitud}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSolicitudesInbox;
